/**
 * SendPricingButton — coach action in the lead detail header.
 *
 * Click → modal opens, calls GET /coach/leads/{id}/pricing-email/preview
 * to fetch the exact HTML the parent will receive, renders it inside an
 * iframe (so the email's inline styles don't leak into the app), then a
 * single "Send to {email}" button POSTs to .../send. Auto-logs as
 * outreach on the server, so the activity log + stale-lead alerts update.
 *
 * Owner spec 2026-02-28: just click, confirm the look, and send.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { DollarSign, Send, Loader2, X, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SendPricingButton({ leadUserId, leadEmail, onSent, compact = false }) {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [preview, setPreview] = useState(null);
    const [error, setError] = useState(null);
    const iframeRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        let cancel = false;
        (async () => {
            setLoading(true);
            setError(null);
            try {
                const r = await axios.get(
                    `${API}/coach/leads/${leadUserId}/pricing-email/preview`,
                    { withCredentials: true },
                );
                if (cancel) return;
                setPreview(r.data);
            } catch (e) {
                if (cancel) return;
                setError(e?.response?.data?.detail || "Couldn't load pricing preview");
            } finally {
                if (!cancel) setLoading(false);
            }
        })();
        return () => { cancel = true; };
    }, [open, leadUserId]);

    // Pump the fetched HTML into the iframe via srcDoc to render the email
    // exactly as Resend will deliver it — same fonts, colors, layout.
    useEffect(() => {
        if (preview?.html && iframeRef.current) {
            iframeRef.current.srcdoc = preview.html;
        }
    }, [preview]);

    const send = async () => {
        setSending(true);
        try {
            const r = await axios.post(
                `${API}/coach/leads/${leadUserId}/pricing-email/send`,
                {},
                { withCredentials: true },
            );
            toast.success(`Pricing sent to ${r.data?.to || leadEmail}`);
            setOpen(false);
            onSent?.();
        } catch (e) {
            toast.error("Couldn't send pricing", {
                description: e?.response?.data?.detail,
            });
        } finally {
            setSending(false);
        }
    };

    return (
        <>
            {compact ? (
                <button
                    type="button"
                    data-testid={`send-pricing-btn-row-${leadUserId}`}
                    onClick={(e) => { e.stopPropagation(); setOpen(true); }}
                    onKeyDown={(e) => { e.stopPropagation(); }}
                    title="Email pricing to this parent"
                    className="inline-flex items-center justify-center w-7 h-7 text-emerald-400 hover:bg-emerald-600 hover:text-white border border-emerald-700/50 hover:border-emerald-500 transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100"
                >
                    <DollarSign className="w-3.5 h-3.5" />
                </button>
            ) : (
                <button
                    type="button"
                    data-testid="send-pricing-btn"
                    onClick={() => setOpen(true)}
                    title="Email the current pricing sheet to this parent"
                    className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase border border-emerald-600/70 bg-emerald-700/15 text-emerald-300 hover:bg-emerald-600 hover:text-white transition-colors px-2.5 py-1.5"
                >
                    <DollarSign className="w-3.5 h-3.5" />
                    Send pricing
                </button>
            )}

            {open && (
                <div
                    data-testid="send-pricing-modal"
                    className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
                    onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
                >
                    <div className="bg-zinc-950 border border-zinc-800 w-full max-w-3xl max-h-[90vh] flex flex-col">
                        <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-800">
                            <div>
                                <div className="text-[10px] tracking-[0.25em] uppercase text-brand-red">
                                    Send pricing
                                </div>
                                <div className="text-sm text-white mt-0.5 truncate">
                                    To{" "}
                                    <span className="text-zinc-300">
                                        {preview?.to || leadEmail || "—"}
                                    </span>
                                </div>
                            </div>
                            <button
                                type="button"
                                data-testid="send-pricing-close"
                                onClick={() => setOpen(false)}
                                className="text-zinc-400 hover:text-white transition-colors p-1"
                                aria-label="Close"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        {preview && !loading && !error && preview.used_fallback && (
                            <div
                                data-testid="send-pricing-fallback-warning"
                                className="px-5 py-2.5 bg-amber-950/60 border-b border-amber-800/50 flex items-start gap-2 text-[11px] text-amber-300 leading-relaxed"
                            >
                                <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                                <span>
                                    No pricing block is marked <strong>&quot;Show in pricing email&quot;</strong> — showing
                                    the default 12-week blocks. To control what parents see, go to{" "}
                                    <strong>Pricing Blocks</strong>, edit the block you want (e.g. 2026), and turn ON
                                    the &quot;Show in pricing email&quot; toggle.
                                </span>
                            </div>
                        )}
                        <div className="flex-1 min-h-0 overflow-hidden bg-black">
                            {loading && (
                                <div className="h-64 flex items-center justify-center">
                                    <Loader2 className="w-5 h-5 text-brand-red animate-spin" />
                                </div>
                            )}
                            {error && !loading && (
                                <div
                                    data-testid="send-pricing-error"
                                    className="p-6 flex items-start gap-3 text-sm text-amber-300"
                                >
                                    <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                    <div>{error}</div>
                                </div>
                            )}
                            {preview && !loading && !error && (
                                <iframe
                                    ref={iframeRef}
                                    data-testid="send-pricing-preview-frame"
                                    title="Pricing email preview"
                                    className="w-full h-[60vh] bg-black border-0"
                                    sandbox=""
                                />
                            )}
                        </div>

                        <div className="px-5 py-3 border-t border-zinc-800 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
                            <div className="text-[11px] text-zinc-400 leading-relaxed">
                                {preview && preview.has_email === false ? (
                                    <span className="text-amber-300">
                                        This profile has no real email on file — add one before sending.
                                    </span>
                                ) : (
                                    <>
                                        Will auto-log as an outreach touch on this lead and bump
                                        the last-contacted timestamp.
                                    </>
                                )}
                            </div>
                            <div className="flex gap-2 justify-end">
                                <button
                                    type="button"
                                    data-testid="send-pricing-cancel"
                                    onClick={() => setOpen(false)}
                                    className="text-[10px] tracking-[0.25em] uppercase border border-zinc-800 text-zinc-300 hover:text-white hover:border-zinc-600 px-3 py-2"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    data-testid="send-pricing-confirm"
                                    onClick={send}
                                    disabled={
                                        sending ||
                                        loading ||
                                        !!error ||
                                        (preview && preview.has_email === false)
                                    }
                                    className="btn-tactical inline-flex disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    {sending ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <Send className="w-4 h-4" />
                                    )}
                                    Send to {preview?.to || leadEmail || "—"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
